'use client'

import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import LocationAutocomplete from './LocationAutocomplete'
import Popup from './Popup'
import SpaceTypeFilters, { getSearchBarIcons } from './SpaceTypeFilters'
import { SVG_ICONS } from './constants'
import { SearchFilters } from './types'
import { useSearchBar } from './useSearchBar'

interface SearchBarProps {
  onSearch?: (filters: SearchFilters) => void
}

const SearchBar = ({ onSearch }: SearchBarProps) => {
  const { t } = useTranslation('common')
  const [openPopup, setOpenPopup] = useState<'types' | 'address' | null>(null)

  const {
    searchFilters,
    handleLocationSelect,
    handleFilterSelect,
    handleResetTypes,
    handleShowSpaces,
    getDisplayText,
    updateSearch
  } = useSearchBar({ onSearch })

  const handleFiltersToggle = () => {
    setOpenPopup(openPopup === 'types' ? null : 'types')
  }

  const searchBarIcons = getSearchBarIcons(searchFilters.spaceTypes)

  return (
    <div className="relative mx-auto w-full max-w-md lg:max-w-md xl:max-w-lg">
      {/* Main Search Bar */}
      <div className="flex h-14 items-center rounded-full border border-ink-100/20 bg-white-500 p-1 shadow-sm transition-[border-color,box-shadow] duration-200 focus-within:border-ink-100/40 focus-within:shadow-md hover:border-ink-100/40 hover:shadow-md">
        {/* Location Input - First Half */}
        <div className="relative flex flex-[3] items-center px-3 py-1.5 lg:px-4">
          <div className="flex flex-1 items-center gap-2 lg:gap-3">
            <div className="shrink-0 text-blue-500">{SVG_ICONS.location}</div>
            <div className="min-w-0 flex-1">
              <label className="block text-[10px] font-semibold tracking-[0.16em] text-ink-200 uppercase">
                {t('SEARCH_BAR.WHERE')}
              </label>
              {/* Do NOT add `type="text"` here. React 19's hydration walker
                  treats `type="text"` as the input default and the CSR DOM
                  construction does not include the attribute, while SSR's
                  ReactDOMServer DOES emit it. The byte-strict attribute
                  comparison then fires #418 on every load — confirmed via
                  at-error captureHydrationDiff on Edge / Bingbot / Chrome
                  across /pension-estacionamiento/* and /renta-bodegas-
                  comerciales/* listing pages, May 2026. Browser default
                  for <input> is text, so omitting the prop has zero
                  functional impact. See CLAUDE.md "Raw <input> with
                  type=text". */}
              <input
                value={searchFilters.search}
                onChange={(e) => {
                  setOpenPopup('address')
                  updateSearch(e.target.value)
                }}
                onFocus={() => setOpenPopup('address')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleLocationSelect({ search: searchFilters.search })
                  }
                }}
                placeholder={t('SEARCH_BAR.ENTER_ADDRESS')}
                className="w-full truncate border-none bg-transparent text-base text-ink-800 outline-none placeholder:text-ink-200 focus:ring-0 md:text-sm"
              />
            </div>

            <button
              type="button"
              aria-label={t('A11Y.CLEAR_SEARCH')}
              onClick={(e) => {
                e.stopPropagation()
                updateSearch('')
                setOpenPopup(null)
              }}
              className={`shrink-0 rounded-full p-1 text-ink-200 transition-colors hover:bg-cream-300 hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 ${
                searchFilters.search ? 'block' : 'hidden'
              }`}
            >
              {SVG_ICONS.close}
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="h-7 w-px bg-ink-100/15" />

        {/* Filters Button - Second Half */}
        <div className="flex flex-1 items-center px-1 lg:px-2">
          <button
            type="button"
            onClick={handleFiltersToggle}
            aria-expanded={openPopup === 'types'}
            className="flex w-full items-center gap-2 rounded-full px-2 py-1.5 text-left transition-colors hover:bg-cream-300/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 lg:gap-2 lg:px-3"
          >
            <div className="flex shrink-0 items-center gap-1">
              {searchBarIcons.slice(0, 2).map((iconOption, index) => (
                <div key={index} className="text-blue-500">
                  {iconOption.muiIcon && (
                    <iconOption.muiIcon
                      className="size-4"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 300,
                        strokeWidth: 0.5
                      }}
                    />
                  )}
                  {iconOption.icon && (
                    <svg
                      className="size-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={iconOption.icon}
                      />
                    </svg>
                  )}
                </div>
              ))}
            </div>
            <div className="min-w-0">
              <span className="block text-[10px] font-semibold tracking-[0.16em] text-ink-200 uppercase">
                {t('SEARCH_BAR.WHAT')}
              </span>
              <span className="block truncate text-sm text-ink-800">
                {getDisplayText()}
              </span>
            </div>
          </button>
        </div>

        {/* Search Button */}
        {/* Icon-only: it had no accessible name. */}
        <button
          type="button"
          aria-label={t('SEARCH_BAR.SEARCH')}
          className="ml-1 flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white-500 shadow-[0_8px_20px_-8px_rgba(28,100,242,0.55)] transition-[background-color,box-shadow,transform] duration-200 hover:bg-blue-600 hover:shadow-[0_10px_24px_-8px_rgba(28,100,242,0.65)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 active:scale-95 lg:ml-1.5"
          onClick={() => handleShowSpaces()}
        >
          {SVG_ICONS.search}
        </button>
      </div>

      {/* Location Autocomplete Popup */}
      <Popup
        isOpen={openPopup === 'address'}
        onClose={() => setOpenPopup(null)}
      >
        <LocationAutocomplete
          onLocationSelect={(placeId, description) => {
            handleLocationSelect({
              placeId,
              description,
              search: description ?? ''
            })
            setOpenPopup('types')
          }}
          searchValue={searchFilters.search}
        />
      </Popup>

      {/* Filters Popup */}
      <Popup
        isOpen={openPopup === 'types'}
        onClose={() => setOpenPopup(null)}
        width="w-full md:w-[600px] lg:w-[700px]"
      >
        <SpaceTypeFilters
          selectedFilters={searchFilters.spaceTypes}
          onFilterSelect={handleFilterSelect}
          onReset={handleResetTypes}
          onShowSpaces={() => {
            handleShowSpaces()
            setOpenPopup(null)
          }}
        />
      </Popup>
    </div>
  )
}

export default SearchBar
